import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import type { EscalationInstance } from '../../types/escalationRuntime.types';
import './EscalationMonitorSlaCell.css';

function formatShortDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return '0m';
  }
  const minutes = Math.ceil(totalSeconds / 60);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remMin = minutes % 60;
    return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

function getOverdueSeconds(instance: EscalationInstance): number {
  if (instance.slaStatus !== 'Overdue' || !instance.nextActionAt) {
    return 0;
  }
  return Math.max(0, Math.floor((Date.now() - new Date(instance.nextActionAt).getTime()) / 1000));
}

interface EscalationMonitorSlaCellProps {
  instance: EscalationInstance;
}

export default function EscalationMonitorSlaCell({
  instance,
}: Readonly<EscalationMonitorSlaCellProps>) {
  const isClosed =
    instance.status === 'Acknowledged' ||
    instance.status === 'Completed' ||
    instance.status === 'Cancelled';

  if (isClosed) {
    return (
      <span className="esc-monitor-sla-closed">
        <CheckCircle2
          size={14}
          strokeWidth={ICON_STROKE_WIDTH}
        />
        Closed
      </span>
    );
  }

  const overdue = instance.slaStatus === 'Overdue';
  const overdueSeconds = getOverdueSeconds(instance);
  const label = overdue
    ? `${formatShortDuration(overdueSeconds)} overdue`
    : `${formatShortDuration(instance.timeRemainingSeconds)} left`;

  const progress = overdue
    ? 100
    : instance.phaseDurationSeconds > 0
      ? Math.min(
          100,
          Math.round((instance.levelElapsedSeconds / instance.phaseDurationSeconds) * 100)
        )
      : instance.slaProgressPercent;

  const tone = overdue ? 'overdue' : instance.slaStatus === 'DueSoon' ? 'duesoon' : 'ontrack';

  return (
    <div className={`esc-monitor-sla-cell esc-monitor-sla-cell--${tone}`}>
      <div
        className="esc-monitor-sla-cell-track"
        aria-hidden="true"
      >
        <div
          className="esc-monitor-sla-cell-fill"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
      <span className="esc-monitor-sla-cell-label">{label}</span>
    </div>
  );
}
